// Websites are stored WITHOUT a scheme — `organizations.website` and
// `vendors.website` hold `acme.com`, and the API's own zod transform strips
// `https?://` on write. The forms show a fixed `https://` prefix beside the
// box, so a pasted full URL used to read "https://https://www.acme.com" until
// it silently changed shape on save.
//
// `www.` is deliberately KEPT: it is part of the host that was typed, and a
// site that only answers on www would get a dead link if we dropped it.

/** Anything of the form `scheme://`, plus the protocol-relative `//`. */
const SCHEME = /^\s*[a-z][a-z0-9+.\-]*:\/\//i;

/**
 * A pasted URL reduced to what gets stored: no scheme, no trailing slash,
 * lower-case host.
 *
 * Only the host is lower-cased — a path can be case-sensitive, and
 * `acme.com/Team` is not `acme.com/team` on every server.
 *
 * Returns '' for anything that is only punctuation, so a caller's
 * `|| undefined` still means "no website".
 */
export function normalizeWebsite(raw: string): string {
  // Mail clients paste links wrapped in angle brackets.
  let s = raw.trim().replace(/^<+/, '').replace(/>+$/, '');
  s = s.replace(SCHEME, '').replace(/^\/\//, '');
  s = s.trim().replace(/\/+$/, '');
  if (s === '') return '';

  const cut = s.search(/[/?#]/);
  const host = cut === -1 ? s : s.slice(0, cut);
  const rest = cut === -1 ? '' : s.slice(cut);
  return host.toLowerCase() + rest;
}

/**
 * A clickable URL for a stored website. The scheme is re-added rather than
 * stored, so every row links the same way regardless of what was pasted.
 */
export function websiteHref(stored: string | null | undefined): string | null {
  const s = normalizeWebsite(stored ?? '');
  return s === '' ? null : `https://${s}`;
}
